import React from 'react';
import { observer } from 'mobx-react';
import { useDroppable } from '@dnd-kit/core';
import OETHeaderCell from './OETHeaderCell';
import OETFirstHeaderCell from './OETFirstHeaderCell';
import DndTypes from './DnDTypes';
import { canDropRowOnHeader, useOETDragState } from './dndContext';
import type { IOETColumn } from '../../stores/model/uimodel/oetcolumn';

/** The (caller-supplied) props the header reads. */
export interface OETHeaderProps {
  columns: IOETColumn[];
  onSelect: (id: string) => void;
  selectable: boolean;
  selected: string | null;
}

/**
 * Render the header of the OET table.
 *
 * The header row is a drop target for top-level rows, so a row can be dropped
 * before the first row (`moveRow(from, 0, group)`, computed in the container's
 * `onDragEnd`). `canDrop` only allows top-level rows and no-ops on the first.
 *
 * - columns: the OET columns
 * - onSelect: callback to be called on select
 * - selectable: is selection enabled?
 * - selected: the currently selected element
 */
const OETHeader = observer(function OETHeader({
  columns,
  onSelect,
  selectable,
  selected,
}: OETHeaderProps) {
  const { setNodeRef } = useDroppable({
    id: 'oet-row-tgt-header',
    data: { kind: 'row-header' },
  });

  const { activeItem, overId } = useOETDragState();
  const isOver = overId === 'oet-row-tgt-header';
  const canDrop =
    activeItem != null && activeItem.type === DndTypes.ROW && canDropRowOnHeader(activeItem);

  return (
    <thead>
      <tr ref={setNodeRef} className={isOver && canDrop ? 'drop-highlight' : ''}>
        <OETFirstHeaderCell />
        {columns.map((col) => (
          <OETHeaderCell
            key={col.object.id}
            column={col}
            selectable={selectable}
            onSelect={onSelect}
            selected={selected}
          />
        ))}
      </tr>
    </thead>
  );
});

export default OETHeader;
